import { differenceInCalendarDays } from 'date-fns';
import { useCallback } from 'react';
import { useLocalStorage } from 'usehooks-ts';

import { LOCAL_STORAGE_KEYS } from '../../utils/common/local-storage-keys';
import useClientSide from '../../utils/hook/useClientSide';
import { MockedTimeManager } from '../../utils/MockedTimeManager';

/** 재노출 정책 — 직전 트리거 이후 재노출까지의 최소 경과 일수 (180일) */
const RESHOW_INTERVAL_DAYS = 180;

/** 재노출 정책 — 트리거 누적 횟수 상한 (이상이면 영구 미노출) */
const MAX_TRIGGER_COUNT = 2;

/** localStorage에 보관하는 인앱 리뷰 시트 트리거 이력 */
interface InAppReviewTriggerHistory {
  /** 시트 호출 누적 횟수 */
  count: number;
  /** 직전 시트 호출 시각 (epoch ms), 미호출 시 null */
  lastTriggeredAt: number | null;
}

const INITIAL_TRIGGER_HISTORY: InAppReviewTriggerHistory = {
  count: 0,
  lastTriggeredAt: null,
};

/**
 * 인앱 리뷰 시트 재노출 정책을 localStorage 기반으로 관리한다 (서버 미기록 — FE 로컬 책임).
 *
 * OS In-App Review 시트는 제출 여부·별점을 공개하지 않으므로, '시트를 띄운(트리거) 횟수'만으로 재노출을 통제한다.
 * - 누적 2회 이상이면 영구 미노출하고, 직전 트리거 후 180일이 지나지 않으면 억제한다
 * - 서버/첫 클라이언트 렌더에서는 항상 억제로 두어 hydration mismatch를 방지한다
 */
export const useInAppReviewReshowPolicy = () => {
  const isClientSide = useClientSide();
  const [triggerHistory, setTriggerHistory] =
    useLocalStorage<InAppReviewTriggerHistory>(
      LOCAL_STORAGE_KEYS.IN_APP_REVIEW_TRIGGER_HISTORY,
      INITIAL_TRIGGER_HISTORY,
      { initializeWithValue: false },
    );

  const now = MockedTimeManager.getInstance().getMockedTime();
  const isWithinReshowInterval =
    triggerHistory.lastTriggeredAt !== null &&
    differenceInCalendarDays(now, new Date(triggerHistory.lastTriggeredAt)) <
      RESHOW_INTERVAL_DAYS;

  const canReshow =
    isClientSide &&
    triggerHistory.count < MAX_TRIGGER_COUNT &&
    !isWithinReshowInterval;

  const recordTrigger = useCallback(
    () =>
      setTriggerHistory((prev) => ({
        count: prev.count + 1,
        lastTriggeredAt: MockedTimeManager.getInstance().getMockedTimestamp(),
      })),
    [setTriggerHistory],
  );

  return { canReshow, recordTrigger };
};
